/**
 * ChunkLoadErrorRecovery — industry-standard guard for long-lived
 * SPAs using React.lazy + webpack code-splitting.
 *
 * Problem:
 *   Dev server (and production CDN deploys) regenerate chunk
 *   filenames when the bundle is rebuilt. A client with a
 *   long-lived tab holds a stale `bundle.js` that references
 *   OLD chunk names. When React.lazy tries to load a chunk that
 *   no longer exists (or whose content has changed), webpack's
 *   chunk loader throws `ChunkLoadError` or `SyntaxError:
 *   Unexpected token '<'` (when the server returns index.html
 *   for a missing chunk). The whole React tree unmounts.
 *
 * Industry-standard fix (React docs, webpack docs, Vite docs):
 *   When a ChunkLoadError is detected in a navigation-adjacent
 *   event, do a ONE-TIME hard reload. A flag in sessionStorage
 *   prevents reload loops (if the reload itself triggers another
 *   ChunkLoadError we show the user a manual-refresh banner
 *   instead of looping).
 *
 * Hooks two surfaces:
 *   1) `window.onerror` — catches ChunkLoadError raised outside
 *      the React render tree (e.g. React.lazy's module loader
 *      that fires promise rejections not wrapped in an
 *      ErrorBoundary).
 *   2) `window.onunhandledrejection` — catches the promise path
 *      that fires when React.lazy's dynamic import fails before
 *      the lazy component ever reaches a Suspense/ErrorBoundary.
 *
 * This module has ZERO dependencies and is safe to import at the
 * top of App.js. It sets up handlers on first call and is
 * idempotent — a second call is a no-op.
 */

const RELOAD_FLAG_KEY = "__wp_chunk_reload_attempt_at";
// If a reload was attempted within this window and we see ANOTHER
// ChunkLoadError, don't reload again — show the banner instead.
const RELOAD_COOLDOWN_MS = 30_000;

let installed = false;

function _isChunkLoadError(err) {
  if (!err) return false;
  const name = err.name || err?.constructor?.name || "";
  const msg = err.message || String(err);
  return (
    name === "ChunkLoadError" ||
    /Loading chunk .* failed/i.test(msg) ||
    /Loading CSS chunk .* failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    // The specific flavor that shows up when the server returned
    // HTML (index.html) for a missing chunk request — the browser
    // tried to evaluate HTML as JS and threw at the first <.
    /Unexpected token ['"]<['"]?/i.test(msg)
  );
}

function _shouldReload() {
  try {
    const last = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (!last) return true;
    const ts = parseInt(last, 10);
    if (!Number.isFinite(ts)) return true;
    return Date.now() - ts > RELOAD_COOLDOWN_MS;
  } catch (_) {
    return true; // storage denied — try reload anyway, once
  }
}

function _markReloadAttempted() {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, Date.now().toString());
  } catch (_) {
    // ignore
  }
}

function _bannerCopy() {
  // Pull the user's chosen language *before* React loads. We persist
  // i18n language to localStorage under either `i18nextLng` or
  // `wp_locale` (see lib/i18n.jsx). Default to English.
  let lang = "en";
  try {
    const v = (localStorage.getItem("i18nextLng") || localStorage.getItem("wp_locale") || "en").toLowerCase();
    lang = v.startsWith("es") ? "es" : "en";
  } catch (_) {
    lang = "en";
  }
  if (lang === "es") {
    return {
      newVersion: "Hay una nueva versión de WashPros disponible.",
      refreshToContinue: "Por favor actualiza para continuar.",
      refreshNow: "Actualizar ahora",
    };
  }
  return {
    newVersion: "A newer version of WashPros is available.",
    refreshToContinue: "Please refresh to continue.",
    refreshNow: "Refresh now",
  };
}

function _showManualBanner() {
  // Only show the banner once per session — if it already exists we skip.
  if (document.getElementById("__wp_chunk_error_banner")) return;
  const copy = _bannerCopy();
  const banner = document.createElement("div");
  banner.id = "__wp_chunk_error_banner";
  banner.setAttribute("role", "alert");
  banner.setAttribute("data-testid", "chunk-error-recovery-banner");
  banner.style.cssText = [
    "position:fixed",
    "top:0",
    "left:0",
    "right:0",
    "z-index:2147483647",
    "padding:12px 16px",
    "background:#0f172a",
    "color:#fff",
    "font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif",
    "font-size:14px",
    "line-height:1.4",
    "border-bottom:2px solid #f59e0b",
    "box-shadow:0 4px 12px rgba(0,0,0,.3)",
    "display:flex",
    "align-items:center",
    "gap:12px",
    "flex-wrap:wrap",
  ].join(";");
  banner.innerHTML = `
    <span style="flex:1;min-width:200px;">
      <strong style="color:#fbbf24;">${copy.newVersion}</strong>
      <span style="color:#cbd5e1;">${copy.refreshToContinue}</span>
    </span>
    <button
      data-testid="chunk-error-reload-btn"
      id="__wp_chunk_reload_btn"
      style="background:#22d3ee;color:#0f172a;border:0;padding:8px 16px;border-radius:6px;font-weight:600;cursor:pointer;font-size:14px;"
    >${copy.refreshNow}</button>
  `;
  document.body.appendChild(banner);
  document
    .getElementById("__wp_chunk_reload_btn")
    ?.addEventListener("click", () => {
      try {
        sessionStorage.removeItem(RELOAD_FLAG_KEY);
      } catch (_) { /* ignore */ }
      window.location.reload();
    });
}

function _handleChunkError(err) {
  if (!_isChunkLoadError(err)) return false;
  // eslint-disable-next-line no-console
  console.warn(
    "[chunk-recovery] Detected ChunkLoadError — attempting recovery",
    err,
  );
  if (_shouldReload()) {
    _markReloadAttempted();
    // Small delay so the current microtask finishes cleanly before
    // the navigation — prevents some "navigation cancelled" logs.
    setTimeout(() => window.location.reload(), 60);
    return true;
  }
  // Second occurrence inside the cooldown window → show the banner
  // and let the user trigger the reload explicitly. Beats an
  // infinite loop.
  _showManualBanner();
  return true;
}

/**
 * Install global chunk-error handlers. Safe to call multiple times.
 */
export function installChunkErrorRecovery() {
  if (installed) return;
  installed = true;

  const origOnError = window.onerror;
  window.onerror = function (message, source, lineno, colno, err) {
    if (_handleChunkError(err) || _handleChunkError({ message })) {
      // Suppress default browser console red; we've handled it.
      return true;
    }
    if (typeof origOnError === "function") {
      return origOnError.apply(this, arguments);
    }
    return false;
  };

  const origOnRejection = window.onunhandledrejection;
  window.onunhandledrejection = function (event) {
    const reason = event?.reason;
    if (_handleChunkError(reason)) {
      event?.preventDefault?.();
      return;
    }
    if (typeof origOnRejection === "function") {
      return origOnRejection.call(this, event);
    }
  };
}

export default installChunkErrorRecovery;
